<template>
    <Layout is-crm :content-class="'content--narrow'">
        <Spinner v-if="loading" />
        <div v-if="customer.id" class="customer">
            <div class="customer__top">
                <h1 v-text="customer.name"></h1>
                <div class="customer__toolbar">
                    <transition name="fade-down">
                        <button
                            v-if="customer.permissions.update && !editMode"
                            type="button"
                            class="button button--icon"
                            @click="editMode = true"
                        >
                            <IconPencil class="button__icon" />
                            <span v-text="$t('misc.edit')"></span>
                        </button>
                    </transition>
                </div>
            </div>

            <transition name="fade-down" mode="out-in">
                <Grid v-if="!editMode" class="customer__info">
                    <Column :span="8">
                        <div v-if="isAdmin" class="descriptions">
                            <div class="descriptions__label">
                                {{ $t('crm.customers.list.type') }}:
                            </div>
                            <div
                                class="descriptions__value"
                                v-text="customer.type_productions.label"
                            ></div>
                        </div>
                        <div class="descriptions">
                            <div class="descriptions__label">
                                {{ $t('crm.customers.list.hook') }}:
                            </div>
                            <div class="descriptions__value" v-text="customer.hook || 'N/A'"></div>
                        </div>
                    </Column>
                </Grid>
                <div v-else class="customer__edit">
                    <CustomerEditForm
                        v-model="customer"
                        :schema="schema"
                        @cancel="editMode = false"
                        @update:model-value="updateCustomer"
                    />
                </div>
            </transition>

            <div class="customer__menu">
                <router-link
                    v-if="customer.permissions.token"
                    class="button button--thick button--big button--outlined button--icon"
                    :to="tokensRoute"
                >
                    <IconKey fill="#ff6b06" class="button__icon" />
                    <span v-text="$t('crm.tokens.list.button')"></span>
                </router-link>

                <WithGlobalPermission permission="CustomerStyleRule.list">
                    <router-link
                        class="button button--thick button--big button--outlined button--icon"
                        :to="stylesheetRoute"
                    >
                        <IconTable fill="#ff6b06" class="button__icon" />
                        <span v-text="$t('crm.stylesheet.menuItem')"></span>
                    </router-link>
                </WithGlobalPermission>

                <WithGlobalPermission permission="CustomerMonthOrderStatistic.list">
                    <router-link
                        class="button button--thick button--big button--outlined button--icon"
                        :to="statisticRoute"
                    >
                        <IconChart fill="#ff6b06" class="button__icon" />
                        <span v-text="$t('crm.statistic.menuItem')"></span>
                    </router-link>
                </WithGlobalPermission>

                <WithGlobalPermission permission="Invite.list">
                    <router-link
                        class="button button--thick button--big button--outlined button--icon"
                        :to="inviteRoute"
                    >
                        <IconUser fill="#ff6b06" class="button__icon" />
                        <span v-text="$t('crm.customers.invites.menuItem')"></span>
                    </router-link>
                </WithGlobalPermission>

                <WithGlobalPermission permission="CustomerMember.list">
                    <CustomerMembers :customer-id="customerId" />
                </WithGlobalPermission>

                <WithGlobalPermission permission="CustomerDefaultSpeaker.list">
                    <router-link
                        class="button button--thick button--big button--outlined button--icon"
                        :to="defaultSpeakersRoute"
                    >
                        <IconSpeaker class="button__icon" fill="#ff6b06" />
                        <span v-text="$t('crm.defaultSpeakers.menuItem')"></span>
                    </router-link>
                </WithGlobalPermission>
            </div>
        </div>
    </Layout>
</template>

<script>
import { fetchCustomersSchema, fetchCustomer } from '../../api/customers';
import { CustomerEditForm, CustomerMembers } from '../../components';
import { Grid, Column } from '@speechki-frontend/shared/ui/Grid';
import Layout from '@speechki-frontend/shared/ui/layout/Layout';
import Spinner from '@speechki-frontend/shared/ui/Spinner';
import {
    IconChart,
    IconPencil,
    IconKey,
    IconTable,
    IconUser,
    IconSpeaker,
} from '@speechki-frontend/shared/ui/icons';
import { WithGlobalPermission } from '@speechki-frontend/core/auth/components';
import { mapGetters, mapActions } from 'vuex';

export default {
    name: 'CustomerDetailsPage',
    components: {
        Layout,
        Spinner,
        Grid,
        Column,
        IconChart,
        IconPencil,
        IconKey,
        IconTable,
        IconUser,
        IconSpeaker,
        CustomerEditForm,
        CustomerMembers,
        WithGlobalPermission,
    },
    data() {
        return {
            customer: {},
            schema: {},
            loading: false,
            editMode: false,
        };
    },
    computed: {
        ...mapGetters(['isAdmin', 'isManager']),

        user() {
            const { pk: id } = this.$store.state.auth.user;
            return { id };
        },
        customerId() {
            const { id } = this.$route.params;
            return id;
        },
        tokensRoute() {
            const { id: customer } = this.customer;
            return { name: 'tokens', params: { customer } };
        },
        stylesheetRoute() {
            const { id } = this.customer;
            return { name: 'customers-id-stylesheet', params: { id } };
        },
        statisticRoute() {
            const { id: customer } = this.customer;
            return { name: 'customers-id-statistic', params: { customer } };
        },
        inviteRoute() {
            const { id } = this.customer;
            return { name: 'customers-id-invites', params: { id } };
        },
        defaultSpeakersRoute() {
            const { id } = this.customer;
            return { name: 'customers-id-default-speakers', params: { id } };
        },
    },
    watch: {
        'customer.name': {
            immediate: true,
            handler(title) {
                if (title !== undefined) {
                    document.title = `${title} - Speechki`;
                }
            },
        },
    },
    async created() {
        this.loading = true;

        try {
            await Promise.all([this.fetchCustomer(), this.fetchCustomersSchema()]);
        } catch (e) {
            console.log(e);
        } finally {
            this.loading = false;
        }
    },
    methods: {
        ...mapActions(['pushToast']),

        // @TODO: redirect (router.push('customers')) if not found
        async fetchCustomer() {
            this.customer = await fetchCustomer(this.customerId);
        },
        async fetchCustomersSchema() {
            const { partial_update } = await fetchCustomersSchema();
            this.schema = partial_update;
        },
        async updateCustomer() {
            this.loading = true;
            this.editMode = false;

            try {
                await this.fetchCustomer();
            } catch (e) {
                console.log(e);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" src="./CustomerDetailsPage.scss"></style>
